/**
 * compileDesignSystem — turn an uploaded design-system bundle into what the
 * sandbox can load, with esbuild (the engine Vite and vitest use).
 *
 * What it guarantees, and what it doesn't:
 *   - `react`, `react/jsx-runtime` and `react-dom` imports are rewritten to the
 *     sandbox's own React (one React, so hooks work);
 *   - it REFUSES, with a message: remote imports (http:, https:, //), dynamic
 *     `import()`, relative imports (nothing else was uploaded), any other bare
 *     import, and in the stylesheet any url() or @import that isn't a data: URI;
 *   - it does NOT detect a copy of React already bundled inside the upload —
 *     a prebuilt, minified bundle no longer says what it contains. That is
 *     caught at runtime instead (a hook call fails, and the sandbox points at
 *     the packaging recipe: build with react, react-dom and react/jsx-runtime
 *     external).
 *
 * `build` is esbuild's (esbuild-wasm in the browser, native under test).
 */
import type { BuildOptions, BuildResult, Plugin } from "esbuild";

export type EsbuildBuild = (
  options: BuildOptions & { write: false },
) => Promise<BuildResult & { outputFiles: { text: string }[] }>;

/** Largest upload taken, per file. */
export const MAX_SOURCE_BYTES = 2 * 1024 * 1024;

/** Bumped when the shims change, so cached compilations are rebuilt. */
export const SHIM_VERSION = 1;

/** The esbuild-wasm release doing the compiling (a test keeps it equal to the
 *  installed package), part of the compilation cache key. */
export const COMPILER_VERSION = "0.28.1";

/** What a cached compilation was made by; a change means compile again. */
export const COMPILATION_ID = `esbuild-${COMPILER_VERSION}+shim-${SHIM_VERSION}`;

export interface CompileInput {
  readonly code: string;
  readonly css: string;
  /** Export names of React, react/jsx-runtime and react-dom, as the sandbox
   *  provides them (read from the same React the sandbox bundles). */
  readonly exportNames: {
    readonly react: readonly string[];
    readonly jsxRuntime: readonly string[];
    readonly reactDom: readonly string[];
  };
}

export type CompileResult =
  | { readonly ok: true; readonly js: string; readonly css: string }
  | { readonly ok: false; readonly errors: readonly string[] };

const IDENT = /^[A-Za-z_$][\w$]*$/;

function shimModule(global: string, names: readonly string[]): string {
  const valid = names.filter((n) => IDENT.test(n) && n !== "default");
  return (
    `const M = globalThis[${JSON.stringify(global)}];\n` +
    `export default M;\n` +
    (valid.length ? `export const { ${valid.join(", ")} } = M;\n` : "")
  );
}

function jsGuard(input: CompileInput): Plugin {
  const shims: Record<string, string> = {
    react: shimModule("__wbReact", input.exportNames.react),
    "react/jsx-runtime": shimModule(
      "__wbJsxRuntime",
      input.exportNames.jsxRuntime,
    ),
    "react/jsx-dev-runtime": shimModule(
      "__wbJsxRuntime",
      input.exportNames.jsxRuntime,
    ),
    "react-dom": shimModule("__wbReactDom", input.exportNames.reactDom),
  };
  return {
    name: "wb-design-system-guard",
    setup(b) {
      b.onResolve({ filter: /.*/ }, (args) => {
        if (args.kind === "entry-point") return undefined;
        const p = args.path;
        if (args.kind === "dynamic-import")
          return { errors: [{ text: `dynamic import("${p}") isn't allowed` }] };
        if (p in shims) return { path: p, namespace: "wb-shim" };
        if (/^(https?:)?\/\//.test(p))
          return { errors: [{ text: `remote import "${p}" isn't allowed` }] };
        if (p.startsWith(".") || p.startsWith("/"))
          return {
            errors: [
              {
                text: `relative import "${p}": upload one file with everything bundled in`,
              },
            ],
          };
        return {
          errors: [
            {
              text: `import "${p}": only react, react-dom and react/jsx-runtime may stay external — bundle the rest in`,
            },
          ],
        };
      });
      b.onLoad({ filter: /.*/, namespace: "wb-shim" }, (args) => ({
        contents: shims[args.path]!,
        loader: "js",
      }));
    },
  };
}

const cssGuard: Plugin = {
  name: "wb-design-system-css-guard",
  setup(b) {
    b.onResolve({ filter: /.*/ }, (args) => {
      if (args.kind === "entry-point") return undefined;
      if (args.path.startsWith("data:")) return { external: true };
      return {
        errors: [
          {
            text:
              args.kind === "import-rule"
                ? `@import "${args.path}" isn't allowed: put all styles in one file`
                : `url(${args.path}) isn't allowed: embed it as a data: URI`,
          },
        ],
      };
    });
  },
};

const messages = (e: unknown): string[] => {
  const errors = (e as { errors?: { text: string }[] }).errors;
  return errors?.length
    ? errors.map((x) => x.text)
    : [e instanceof Error ? e.message : String(e)];
};

export async function compileDesignSystem(
  build: EsbuildBuild,
  input: CompileInput,
): Promise<CompileResult> {
  const tooBig = [
    ["bundle", input.code],
    ["stylesheet", input.css],
  ]
    .filter(([, s]) => new TextEncoder().encode(s).length > MAX_SOURCE_BYTES)
    .map(
      ([what]) => `the ${what} is over ${MAX_SOURCE_BYTES / 1024 / 1024} MB`,
    );
  if (tooBig.length) return { ok: false, errors: tooBig };

  const errors: string[] = [];
  let js = "";
  try {
    const out = await build({
      stdin: {
        contents: input.code,
        loader: "js",
        sourcefile: "design-system.js",
      },
      bundle: true,
      write: false,
      format: "esm",
      platform: "browser",
      target: "es2020",
      minify: true,
      logLevel: "silent",
      plugins: [jsGuard(input)],
    });
    js = out.outputFiles[0]?.text ?? "";
    // Every import esbuild can see was resolved (or refused) above; an
    // import() it couldn't resolve — import(variable) — stays in the output
    // silently. In minified output without comments, that is dynamic loading.
    if (/(^|[^\w$.])import\s*\(/.test(js))
      errors.push("dynamic import() isn't allowed");
  } catch (e) {
    errors.push(...messages(e));
  }

  let css = "";
  try {
    const out = await build({
      stdin: {
        contents: input.css,
        loader: "css",
        sourcefile: "design-system.css",
      },
      bundle: true,
      write: false,
      minify: true,
      logLevel: "silent",
      plugins: [cssGuard],
    });
    css = out.outputFiles[0]?.text ?? "";
  } catch (e) {
    errors.push(...messages(e));
  }

  return errors.length ? { ok: false, errors } : { ok: true, js, css };
}
